import { useGetPatientHealthSummaryRecords } from '@hooks/api/patient-record';

import { NO_KNOWN_ALLERGY_MAP } from '@models/allergy';

import { formatName, formatPhone } from '@utils/format';
import {
  getStackedImmunizationLabels,
  sortRecordsByDateDesc,
} from '@utils/health-summary';
import {
  getBloodTypeDisplay,
  getHeightDisplay,
  getWeightDisplay,
} from '@utils/records';
import { sortMedications } from '@utils/sort/records';

export type PdfInsurancePlan = {
  provider: string;
  planNumber: string | null;
};

export type PdfEmergencyContact = {
  priority: string;
  name: string;
  phone: string;
};

export type PdfHealthcareContact = {
  role: string;
  name: string;
  phone: string;
};

export type PdfAllergy = {
  name: string;
  severe: boolean;
  categories: string[];
};

export type PdfPatientData = {
  name: string | null;
  dob: string | null;
  age: number | null;
  sex: string | null;
  address: string | null;
  phone: string;
  email: string | null;
  initials: string;
  bloodType: string | null;
  height: string | null;
  weight: string | null;
  allergies: PdfAllergy[];
  noKnownAllergies: string[];
  conditions: string[];
  procedures: string[];
  medications: string[];
  immunizations: string[];
  coverage: PdfInsurancePlan[];
  organDonation: string[];
  bloodProducts: string[];
  heroicMeasures: string[];
  powerOfAttorney: { name: string; phone: string } | null;
  emergencyContacts: PdfEmergencyContact[];
  healthcareContacts: PdfHealthcareContact[];
};

const EMERGENCY_PRIORITY_LABELS: Record<number, string> = {
  0: 'Primary',
  1: 'Secondary',
};

function getInitials(firstName?: string | null, lastName?: string | null) {
  const first = firstName?.trim().charAt(0).toUpperCase() ?? '';
  const last = lastName?.trim().charAt(0).toUpperCase() ?? '';
  return first + last || '?';
}

export function useHealthSummaryPdfData(): {
  pdfData: PdfPatientData | null;
  loading: boolean;
  /** Query finished without usable patient data (Apollo error or missing patient). */
  hasLoadError: boolean;
} {
  const { data, loading, error } = useGetPatientHealthSummaryRecords({
    allergy: true,
    condition: true,
    immunization: true,
    medication_statement: true,
    procedure: true,
    legal_directives: true,
    related_person: true,
    coverage: true,
    patient: true,
    blood_type: true,
    height_body_observation: true,
    weight_body_observation: true,
    care_team: true,
  });

  if (loading) {
    return { pdfData: null, loading: true, hasLoadError: false };
  }

  if (error || !data.patient) {
    return { pdfData: null, loading: false, hasLoadError: true };
  }

  const { patient } = data;

  const noKnownAllergies: string[] = [];
  const allergies: PdfAllergy[] = [];
  for (const record of data.allergy) {
    if (record.noAllergies) {
      noKnownAllergies.push(
        NO_KNOWN_ALLERGY_MAP[record.noAllergies] ?? record.noAllergies
      );
    } else if (record.substance) {
      allergies.push({
        name: record.substance,
        severe: record.isSevere,
        categories: record.allergyType
          ? record.allergyType
              .split(',')
              .map((category) => category.trim().toLowerCase())
              .filter(Boolean)
          : [],
      });
    }
  }

  const organDirective = data.legal_directives.find((d) => d.isOrganDonation());
  const bloodDirective = data.legal_directives.find((d) => d.isBloodProducts());
  const heroicDirective = data.legal_directives.find((d) =>
    d.isHeroicMeasures()
  );

  const { emergencyContacts, powerOfAttorney } = data.related_persons.reduce<{
    emergencyContacts: PdfEmergencyContact[];
    powerOfAttorney: { name: string; phone: string } | null;
  }>(
    (acc, person) => {
      if (person.isEmergencyContact()) {
        const index = acc.emergencyContacts.length;
        acc.emergencyContacts.push({
          priority: EMERGENCY_PRIORITY_LABELS[index] ?? 'Contact',
          name: person.name ?? '',
          phone: formatPhone(person.phone),
        });
      } else if (person.isPowerOfAttorney() && !acc.powerOfAttorney) {
        acc.powerOfAttorney = {
          name: person.name ?? '',
          phone: formatPhone(person.phone),
        };
      }
      return acc;
    },
    { emergencyContacts: [], powerOfAttorney: null }
  );

  const participants = data.care_team?.participants ?? [];
  const healthcareContacts: PdfHealthcareContact[] = participants.map((p) => ({
    role: p.role_name,
    name: formatName(p.prefix, p.first_name, p.last_name) ?? '',
    phone: formatPhone(p.phone),
  }));

  const pdfData: PdfPatientData = {
    name: formatName(patient.first_name, patient.last_name) ?? null,
    dob: patient.getBirthday(),
    age: patient.getAge(),
    sex: patient.gender ?? patient.sex ?? null,
    address: patient.primaryAddress,
    phone: formatPhone(patient.phone),
    email: patient.email ?? null,
    initials: getInitials(patient.first_name, patient.last_name),
    bloodType: getBloodTypeDisplay(data.blood_type),
    height: getHeightDisplay(data.height_body_observation ?? undefined) ?? null,
    weight: getWeightDisplay(data.weight_body_observation ?? undefined) ?? null,
    allergies,
    noKnownAllergies,
    conditions: sortRecordsByDateDesc(data.condition, (c) => c.getOnsetDate())
      .map((c) => c.condition ?? '')
      .filter(Boolean),
    procedures: sortRecordsByDateDesc(data.procedure, (p) =>
      p.getProcedureDate()
    )
      .map((p) => p.procedureName)
      .filter(Boolean),
    medications: [...data.medication_statement]
      .sort(sortMedications)
      .map((m) => m.medication ?? '')
      .filter(Boolean),
    immunizations: getStackedImmunizationLabels(
      data.immunization,
      (i) => i.vaccineName,
      (i) => i.getImmunizationDate()
    ),
    coverage: data.coverage.map((c) => ({
      provider: c.provider ?? '',
      planNumber: c.planNumber ?? null,
    })),
    organDonation:
      organDirective?.getDirectiveText('specific-organs').filter(Boolean) ?? [],
    bloodProducts:
      bloodDirective?.getDirectiveText('blood-products').filter(Boolean) ?? [],
    heroicMeasures:
      heroicDirective?.getDirectiveText('heroic-measures').filter(Boolean) ??
      [],
    powerOfAttorney,
    emergencyContacts,
    healthcareContacts,
  };

  return { pdfData, loading: false, hasLoadError: false };
}
